import React from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import Layout from "../components/layout/Layout";
import { ArrowLeft, FileText } from "lucide-react";

export default function TermsPage() {
  return (
    <Layout>
      <div id="terms_page" className="min-h-screen bg-neutral-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-neutral-400 hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="text-lg font-extralight">На главную</span>
            </Link>

            <div className="flex items-center gap-4 mb-10">
              <div className="w-12 h-12 rounded-md bg-primary-500/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-primary-400" />
              </div>
              <div>
                <h1 className="text-3xl lg:text-4xl font-medium text-white">
                  Пользовательское соглашение
                </h1>
                <p className="text-lg font-extralight text-neutral-500 mt-1">
                  Редакция от 19 января 2025 года
                </p>
              </div>
            </div>

            <div className="space-y-10 text-lg font-extralight text-neutral-300 leading-relaxed">
              <section>
                <h2 className="text-xl font-medium text-white mb-4">1. Общие положения</h2>
                <p className="mb-3">
                  1.1. Настоящее Пользовательское соглашение (далее — Соглашение) регулирует отношения между
                  администрацией информационной платформы "Обстучим" (далее — Платформа, Администрация) и
                  физическим или юридическим лицом (далее — Пользователь), использующим Платформу.
                </p>
                <p className="mb-3">
                  1.2. Платформа представляет собой информационный сервис, обеспечивающий техническую
                  возможность для взаимодействия между Пользователями, выступающими в роли Клиентов и
                  Экспертов, с целью обмена профессиональными знаниями и опытом.
                </p>
                <p className="mb-3">
                  1.3. Регистрируясь на Платформе, Пользователь подтверждает, что полностью ознакомился
                  с условиями настоящего Соглашения и принимает их в полном объёме. В случае несогласия
                  с какими-либо условиями Пользователь обязан прекратить использование Платформы.
                </p>
                <p>
                  1.4. Администрация вправе в одностороннем порядке изменять условия настоящего Соглашения.
                  Актуальная версия размещается на данной странице. Продолжение использования Платформы
                  после внесения изменений означает согласие с новой редакцией Соглашения.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">2. Предмет соглашения</h2>
                <p className="mb-3">
                  2.1. Платформа оказывает исключительно информационные услуги, предоставляя
                  Пользователям техническую возможность размещать информацию о себе (для Экспертов)
                  и находить подходящих специалистов для консультаций (для Клиентов).
                </p>
                <p className="mb-3">
                  2.2. Платформа не является стороной правоотношений между Клиентом и Экспертом.
                  Все договорённости о характере, объёме и качестве оказываемых консультационных
                  услуг достигаются непосредственно между Пользователями.
                </p>
                <p>
                  2.3. Платформа не оказывает консультационных, юридических, финансовых, медицинских
                  и иных профессиональных услуг. Любая информация, размещённая на Платформе, носит
                  исключительно информационный характер и не может рассматриваться как профессиональная
                  рекомендация.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">3. Статус Платформы и ограничение ответственности</h2>
                <p className="mb-3">
                  3.1. Платформа выступает исключительно как информационный посредник, обеспечивающий
                  техническую инфраструктуру для коммуникации между Пользователями, и не несёт
                  ответственности за содержание, достоверность, полноту и качество информации,
                  размещаемой Пользователями.
                </p>
                <p className="mb-3">
                  3.2. Администрация не несёт ответственности за:
                </p>
                <ul className="list-none space-y-2 ml-6 mb-3">
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-primary-500 mt-1.5 flex-shrink-0">—</span>
                    качество, полноту и достоверность консультаций, предоставленных Экспертами
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-primary-500 mt-1.5 flex-shrink-0">—</span>
                    любые последствия (прямые или косвенные) использования информации, полученной на Платформе
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-primary-500 mt-1.5 flex-shrink-0">—</span>
                    убытки, возникшие в результате действий или бездействия Пользователей
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-primary-500 mt-1.5 flex-shrink-0">—</span>
                    невозможность использования Платформы по причинам, не зависящим от Администрации
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-primary-500 mt-1.5 flex-shrink-0">—</span>
                    действия третьих лиц, в том числе нарушение работы каналов связи
                  </li>
                </ul>
                <p>
                  3.3. Пользователь самостоятельно несёт ответственность за оценку рисков, связанных
                  с использованием информации, полученной через Платформу, и за принятие решений
                  на основании такой информации.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">4. Права и обязанности Пользователя</h2>
                <p className="mb-3">
                  4.1. Пользователь обязуется:
                </p>
                <ul className="list-none space-y-2 ml-6 mb-3">
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-primary-500 mt-1.5 flex-shrink-0">—</span>
                    предоставлять достоверную информацию при регистрации и в процессе использования Платформы
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-primary-500 mt-1.5 flex-shrink-0">—</span>
                    не использовать Платформу для целей, противоречащих законодательству Российской Федерации
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-primary-500 mt-1.5 flex-shrink-0">—</span>
                    соблюдать нормы делового общения при взаимодействии с другими Пользователями
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-primary-500 mt-1.5 flex-shrink-0">—</span>
                    не размещать контент, нарушающий права третьих лиц, включая авторские и смежные права
                  </li>
                </ul>
                <p>
                  4.2. Пользователь имеет право в любой момент удалить свою учётную запись, направив
                  соответствующий запрос Администрации.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">5. Права Администрации</h2>
                <p className="mb-3">
                  5.1. Администрация вправе без предупреждения и объяснения причин:
                </p>
                <ul className="list-none space-y-2 ml-6">
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-primary-500 mt-1.5 flex-shrink-0">—</span>
                    ограничить или прекратить доступ Пользователя к Платформе
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-primary-500 mt-1.5 flex-shrink-0">—</span>
                    удалить контент, нарушающий условия настоящего Соглашения или законодательство РФ
                  </li>
                  <li className="text-lg font-extralight text-neutral-400 flex items-start gap-2">
                    <span className="text-primary-500 mt-1.5 flex-shrink-0">—</span>
                    проводить профилактические работы, временно ограничивая доступ к Платформе
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">6. Интеллектуальная собственность</h2>
                <p className="mb-3">
                  6.1. Все материалы Платформы, включая дизайн, программный код, товарные знаки и логотипы,
                  являются объектами интеллектуальной собственности Администрации и охраняются в соответствии
                  с законодательством Российской Федерации.
                </p>
                <p>
                  6.2. Использование материалов Платформы без письменного согласия Администрации запрещено.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">7. Порядок расчётов</h2>
                <p className="mb-3">
                  7.1. Оплата консультационных услуг осуществляется Клиентом через Платформу в порядке
                  и на условиях, определённых в соответствующем разделе Платформы.
                </p>
                <p className="mb-3">
                  7.2. Платформа вправе взимать комиссию за предоставление информационных услуг.
                  Размер комиссии указывается в соответствующем разделе Платформы и может быть
                  изменён Администрацией в одностороннем порядке.
                </p>
                <p>
                  7.3. Возврат денежных средств осуществляется в порядке, предусмотренном
                  законодательством Российской Федерации и правилами Платформы.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">8. Разрешение споров</h2>
                <p className="mb-3">
                  8.1. Все споры и разногласия разрешаются путём переговоров. В случае невозможности
                  достижения согласия спор передаётся на рассмотрение в суд по месту нахождения
                  Администрации в соответствии с действующим законодательством Российской Федерации.
                </p>
                <p>
                  8.2. Признание судом какого-либо положения настоящего Соглашения недействительным
                  не влечёт за собой недействительность остальных положений.
                </p>
              </section>

              <section>
                <h2 className="text-xl font-medium text-white mb-4">9. Заключительные положения</h2>
                <p className="mb-3">
                  9.1. Настоящее Соглашение регулируется и толкуется в соответствии с законодательством
                  Российской Федерации.
                </p>
                <p className="mb-3">
                  9.2. По всем вопросам, связанным с исполнением настоящего Соглашения, Пользователь
                  может обратиться к Администрации через контактные данные, указанные на Платформе.
                </p>
                <p>
                  9.3. Бездействие Администрации в случае нарушения Пользователем условий Соглашения
                  не лишает Администрацию права на принятие соответствующих мер в дальнейшем.
                </p>
              </section>

              <div className="pt-8 border-t border-neutral-800/50">
                <p className="text-lg font-extralight text-neutral-500">
                  Если у вас остались вопросы, вы можете связаться с нами через раздел{" "}
                  <Link to="/contact" className="text-primary-400 hover:text-primary-300 transition-colors">
                    Контакты
                  </Link>.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
}
